import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cloneDeep, mergeWith } from 'lodash';
import pdfTemplateConfig from './config';
import { getDefaultOption, handleResponseErrors } from '@src/Services/Helpers/Actions';
import API from '../Api';

export const fetchTemplateByName = createAsyncThunk(
  "pdfTemplate/fetchTemplateByName",
  async (templateName, { fulfillWithValue, rejectWithValue }) => {
    const response = await API.getTemplateByName(templateName)
      .then(res => res)
      .catch(e => handleResponseErrors(e));
    if (response?.status === 200) {
      return fulfillWithValue({ templateName, data: response?.data?.data });
    }
    return rejectWithValue(response);
  }
);

/**
 * Vendor order template. It lives in this slice (and in the same `quotation_templates`
 * table) so one `/versions` call syncs every template; the quotation list skips it
 * because `TEMPLATE_COMPONENTS` has no entry for it. Exported so the vendor page can
 * fall back to it before the slice has rehydrated.
 */
export const VENDOR_ORDER_TEMPLATE = {
  label: 'Template 1',
  template_name: 'vendor_order_template_1',
  version: '1.00',
  ...pdfTemplateConfig.vendor_order_template_1
};

const initialState = {
  templates: [
    {
      label: 'Template 1',
      template_name: 'template_1',
      version: '1.01',
      ...pdfTemplateConfig.template_1
    },
    {
      label: 'Template 2',
      template_name: 'template_2',
      version: '1.01',
      ...pdfTemplateConfig.template_2
    },
    {
      label: 'Template 3',
      template_name: 'template_3',
      version: '1.00',
      ...pdfTemplateConfig.template_3
    },
    VENDOR_ORDER_TEMPLATE
  ],
  active_template: 'template_1',
  isUpdating: false,
  versions: {
    template_1: 1.01,
    template_2: 1.01,
    template_3: 1.00,
    vendor_order_template_1: 1.00
  },
};

const parseMaybeJson = (value, depth = 0) => {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed || !['{', '[', '"'].includes(trimmed[0])) return value;

  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'string' && depth < 1 ? parseMaybeJson(parsed, depth + 1) : parsed;
  } catch {
    return value;
  }
};

const normalizeTemplatePatch = (patch) => {
  const next = cloneDeep(patch);

  next.configs = parseMaybeJson(next.configs);
  next.footer_data = parseMaybeJson(next.footer_data);
  next.attachments = parseMaybeJson(next.attachments);

  if (next.footer_data && typeof next.footer_data === 'object' && !Array.isArray(next.footer_data)) {
    next.footer_data.data = parseMaybeJson(next.footer_data.data);
    next.footer_data.attachments = parseMaybeJson(next.footer_data.attachments);
  }

  return next;
};

const mergeTemplatePatch = (template, patch) =>
  mergeWith(cloneDeep(template), patch, (_objValue, srcValue) => {
    if (Array.isArray(srcValue)) return srcValue;
    return undefined;
  });

export const pdfTemplateSlice = createSlice({
  name: 'pdfTemplate',
  initialState,
  reducers: {
    setTemplateVersions: (state, action) => {
      state.versions = action.payload;
    },
    setActiveTemplate: (state, action) => {
      state.active_template = action.payload;
    },
    setIsTemplatesUpdating: (state, action) => {
      state.isUpdating = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplateByName.fulfilled, (state, action) => {
        const { templateName, data: responseData } = action.payload;
        const oldTemplates = state?.templates || [];
        const oldTemplate = getDefaultOption(templateName, oldTemplates, 'template_name');
        if (!oldTemplate) return;

        /** Backend may return payload under `item` or at root — shallow spread missed nested `configs` / `footer_data` */
        const patch = responseData?.item ?? responseData;
        const plainPatch =
          patch && typeof patch === 'object' && !Array.isArray(patch)
            ? normalizeTemplatePatch(patch)
            : null;
        if (plainPatch) {
          delete plainPatch.template_name;
        }

        const newTemplate = plainPatch
          ? mergeTemplatePatch(oldTemplate, plainPatch)
          : oldTemplate;

        state.templates = oldTemplates.map((item) =>
          item.template_name === templateName ? newTemplate : item
        );

        const v = responseData?.version ?? plainPatch?.version ?? newTemplate?.version;
        if (v != null && v !== '') {
          state.versions = { ...state.versions, [templateName]: v };
        }
      });
  }
});

export const {
  setTemplateVersions,
  setActiveTemplate,
  setIsTemplatesUpdating
} = pdfTemplateSlice.actions;

export default pdfTemplateSlice.reducer;
